import {
	Customer,
	CustomerAccessToken,
	CustomerUpdateInput,
	CustomerUpdatePayload,
	// Order,
} from '@local/types/storefront-api-types'
// import {unwindEdges} from '@good-idea/unwind-edges'
import {postWithLocale, fetchWithLocale} from '../useShopifyStore'

export interface ShopifyCustomerProps {
	loading: boolean,
	customerAccessToken?: CustomerAccessToken | null,
	customer?: Customer | null,
	rechargeCustomer?: any | null,
	// orders?: Order[],
	referralLink: string,
	initCustomer: () => Promise<Customer | null>,
	getDetails: (preventSet?: boolean) => Promise<Customer | null>,
	updateDetails: (input: CustomerUpdateInput, metafields?: any[]) => Promise<CustomerUpdatePayload> | null,
	// getOrders: (limit?: number) => Promise<Order[] | null>,
	getRechargeCustomer: () => Promise<any | null>,
	getRechargeSubscriptions: () => Promise<any | null>,
	getRechargePaymentMethods: () => Promise<any | null>,
	getReferralLink: () => Promise<string | null>,
	sendPaymentMethodEmail: (id: string) => Promise<any | null>,
}

export const shopifyCustomerStore = (set, get, store): ShopifyCustomerProps => ({
	loading: true,
	referralLink: '',

	initCustomer: async () => {
		let customerAccessToken = get().customerAccessToken
		let customer

		if (customerAccessToken) {
			if (new Date(customerAccessToken.expiresAt).getTime() <= new Date().getTime()) {
				// invalidate, user will need to sign in again
				customerAccessToken = null
			} else {
				customer = await get().getDetails()
				if (!customer) {
					customerAccessToken = null
				} else {
					await get().getAddresses()
				}
			}
		}

		set({
			customerAccessToken,
			loading: false,
		})

		return customer
	},

	getDetails: async () => {
		const customerAccessToken = get().customerAccessToken?.accessToken
		if (!customerAccessToken) return null
		set({loading: true})

		const customer = await fetchWithLocale('/api/shopify/customer/get', {searchParams: {customerAccessToken}})

		set({
			customer,
			loading: false,
		})

		return customer
	},

	updateDetails: async (customerInput: CustomerUpdateInput, metafields: any[] = []) => {
		const customerAccessToken = get().customerAccessToken?.accessToken
		if (!customerAccessToken) return null
		set({loading: true})

		const result = await postWithLocale('/api/shopify/customer/update', {customerAccessToken, customer: customerInput})

		const customer: Customer = {
			...get().customer,
			...(result || {}),
		}

		if (customer?.id && metafields?.length) {
			// accumulate fields which need an update
			const _metafields = [] as any[]

			// check each field to see if an update is required,
			// if required, add to _metafields
			metafields.forEach((field) => {
				const currentField = customer.metafields?.find(metafield => metafield?.key === field.key)
				if (currentField?.value !== field.value) {
					const currentFieldId = currentField?.id
					if (currentFieldId) {
						_metafields.push({id: currentFieldId, value: field.value})
					} else {
						_metafields.push(field)
					}
				}
			})

			// if there are any updates required,
			// make an API call
			if (_metafields?.length) {
				await postWithLocale('/api/shopify/customer/update-attributes', {
					id: customer.id,
					metafields: _metafields,
				})
			}
		}

		set({
			customer,
			loading: false,
		})

		if (customerInput.password) {
			// customerAccessToken will already be invalidated
			await get().signOut({noInvalidateToken: true})
		}

		return result.customerUpdate
	},

	// getOrders: async (limit = 250) => {
	// 	const customerAccessToken = get().customerAccessToken?.accessToken
	// 	if (!customerAccessToken) return null
	// 	set({loading: true})
	// 	const result = await fetchWithLocale('/api/shopify/customer/get-orders', {
	// 		searchParams: {customerAccessToken, limit},
	// 	})
	// 	const orders = unwindEdges(result?.customer?.orders)[0] as Order[]
	// 	set({orders, loading: false})
	// 	return orders
	// },

	sendPaymentMethodEmail: async (paymentMethodId) => {
		return await postWithLocale('/api/shopify/customer/send-update-payment-url', {id: paymentMethodId})
	},

	getReferralLink: async () => {
		const res = await postWithLocale('/api/yotpo/get-referrer', {email: get().customer?.email})
		set({referralLink: res.referral_link})
		return res
	},

	getRechargeCustomer: async () => {
		const customer = get().customer
		if (!customer) return false

		let rechargeCustomer = get().rechargeCustomer

		// get recharge user id
		if (!rechargeCustomer) {
			rechargeCustomer = await postWithLocale('/api/recharge/get-customer', {
				id: customer.id,
				email: customer.email,
			})

			if (rechargeCustomer?.id) {
				set({rechargeCustomer})
			}
		}

		return rechargeCustomer
	},

	getRechargeSubscriptions: async () => {
		const rechargeCustomer = get().rechargeCustomer || await get().getRechargeCustomer()

		if (rechargeCustomer.id) {
			const subscriptions = await postWithLocale('/api/recharge/get-subscriptions', {rechargeId: rechargeCustomer.id})
			return subscriptions
		}

		return null
	},

	getRechargePaymentMethods: async () => {
		const customer = get().customer
		if (!customer) return false

		const rechargeCustomer = await get().getRechargeCustomer()
		if (rechargeCustomer) {
			const rechargePaymentMethods = await postWithLocale('/api/recharge/get-payment-methods', {
				id: customer.id,
				rechargeId: rechargeCustomer.id,
			})
			return rechargePaymentMethods?.paymentMethods
		}
	},
})
